import React from "react";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { EventContentArg } from "@fullcalendar/core";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { WorkOrder, WorkOrderStatusCode, WorkOrderPriorityCode } from "../../models/models";
import { typeColors } from "./calendarStyles";
import dayjs from "dayjs";

/** Avatar background colors — rotate by assignee name hash */
const avatarColors = [
  "#D4572B", "#3B82F6", "#8C6BD4", "#2D8C4E",
  "#E8A817", "#6B7280", "#0C1117", "#C0392B",
];

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

/** Format time range from event start/end */
function formatTimeRange(wo: WorkOrder): string | null {
  const start = wo.scheduledStartDate;
  if (!start) return null;
  const startTime = dayjs(start).format("HH:mm");
  const detail = wo as any;
  if (detail.scheduledEndDate) {
    return `${startTime}  ~  ${dayjs(detail.scheduledEndDate).format("HH:mm")}`;
  }
  if (wo.estimatedDurationMinutes) {
    const end = dayjs(start).add(wo.estimatedDurationMinutes, "minute");
    return `${startTime}  ~  ${end.format("HH:mm")}`;
  }
  return startTime;
}

interface ScheduleEventContentProps {
  eventInfo: EventContentArg;
}

const ScheduleEventContent: React.FC<ScheduleEventContentProps> = ({ eventInfo }) => {
  const { translate } = useTranslation("operations");
  const wo: WorkOrder | undefined = eventInfo.event.extendedProps?.workOrder;
  const { isPhone } = useBreakpoint();
  if (!wo) return null;

  const isMonthView = eventInfo.view.type === "dayGridMonth";
  const isAllDayEvent = eventInfo.event.allDay;
  const isOverdue =
    wo.dueDate &&
    dayjs(wo.dueDate).isBefore(dayjs()) &&
    ![WorkOrderStatusCode.Completed, WorkOrderStatusCode.Verified, WorkOrderStatusCode.Cancelled].includes(
      wo.workOrderStatusId
    );
  const isRecurring = !!wo.recurrenceParentId;
  const typeColor = typeColors[wo.workOrderTypeId] || typeColors[5];
  const timeRange = formatTimeRange(wo);

  // Priority badge — only show for Critical and High
  const showPriorityBadge =
    wo.priorityCode === WorkOrderPriorityCode.Critical ||
    wo.priorityCode === WorkOrderPriorityCode.High;
  const priorityLabel = translate(
    wo.priorityCode === WorkOrderPriorityCode.Critical ? "priorities.critical" : "priorities.high"
  );

  // Month view and all-day events in week/day views: compact single-line with left type-color border
  // All-day row has limited vertical space, so use the same compact rendering as month view.
  if (isMonthView || isAllDayEvent) {
    return (
      <div className="flex items-center gap-1 overflow-hidden w-full px-1 py-px">
        {isOverdue && (
          <span className="text-[9px] text-[#D4572B] flex-shrink-0">!</span>
        )}
        {isRecurring && (
          <span className="text-[9px] text-mid-muted flex-shrink-0">{"\u21BB"}</span>
        )}
        <span
          className="font-mono text-[9px] whitespace-nowrap overflow-hidden text-ellipsis text-mid-muted"
        >
          {wo.number}
        </span>
        <span
          className="font-sora text-[10px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis text-mid-charcoal"
        >
          {wo.title}
        </span>
      </div>
    );
  }

  // Phone Day view: compact — time + title only, small priority dot
  if (isPhone) {
    const priorityDotColor =
      wo.priorityCode === WorkOrderPriorityCode.Critical
        ? "#D4572B"
        : wo.priorityCode === WorkOrderPriorityCode.High
          ? "#E8A817"
          : null;

    return (
      <div className="flex flex-col overflow-hidden w-full h-full py-0.5 px-1 gap-0.5">
        {/* Row 1: time range + priority dot */}
        <div className="flex items-center gap-1">
          {timeRange && (
            <span
              className="whitespace-nowrap overflow-hidden text-ellipsis"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#8C9099" }}
            >
              {timeRange}
            </span>
          )}
          <div className="flex-1" />
          {priorityDotColor && (
            <span
              className="flex-shrink-0 rounded-full"
              style={{ width: 6, height: 6, backgroundColor: priorityDotColor }}
            />
          )}
        </div>
        {/* Row 2: title (2 lines max) */}
        <div
          className="overflow-hidden leading-tight"
          style={{
            fontFamily: "'Sora', sans-serif",
            fontSize: "11px",
            fontWeight: 600,
            color: isOverdue ? "#D4572B" : "#0C1117",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical" as any,
          }}
        >
          {wo.title}
        </div>
      </div>
    );
  }

  // Week/Day view: white card with colored left border
  return (
    <div className="flex flex-col overflow-hidden w-full h-full py-1 px-1.5 gap-0.5">
      {/* Row 1: WO number + badges */}
      <div className="flex items-center gap-1">
        <span className="font-mono text-[9px] text-mid-muted whitespace-nowrap">
          {wo.number}
        </span>
        {isRecurring && (
          <span className="text-[10px] text-mid-muted flex-shrink-0 leading-none">{"\u21BB"}</span>
        )}
        <div className="flex-1" />
        {isOverdue && (
          <span
            className="font-sora text-[8px] font-semibold px-1 py-px rounded-sm flex-shrink-0"
            style={{
              backgroundColor: "rgba(212, 87, 43, 0.1)",
              color: "#D4572B",
              border: "1px solid rgba(212, 87, 43, 0.2)",
            }}
          >
            Overdue
          </span>
        )}
        {!isOverdue && showPriorityBadge && (
          <span
            className="font-sora text-[8px] font-semibold px-1 py-px rounded-sm flex-shrink-0"
            style={{
              backgroundColor:
                wo.priorityCode === WorkOrderPriorityCode.Critical
                  ? "rgba(212, 87, 43, 0.1)"
                  : "rgba(232, 168, 23, 0.1)",
              color:
                wo.priorityCode === WorkOrderPriorityCode.Critical
                  ? "#D4572B"
                  : "#E8A817",
              border: `1px solid ${
                wo.priorityCode === WorkOrderPriorityCode.Critical
                  ? "rgba(212, 87, 43, 0.2)"
                  : "rgba(232, 168, 23, 0.2)"
              }`,
            }}
          >
            {priorityLabel}
          </span>
        )}
      </div>

      {/* Row 2: Title */}
      <div
        className="font-sora text-[12px] font-semibold leading-tight overflow-hidden"
        style={{
          color: isOverdue ? "#D4572B" : "#0C1117",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" as any,
        }}
      >
        {wo.title}
      </div>

      {/* Row 3: Machine name */}
      {wo.machineName && (
        <span className="font-sora text-[10px] text-mid-muted whitespace-nowrap overflow-hidden text-ellipsis">
          {wo.machineName}
        </span>
      )}

      {/* Spacer to push bottom row down */}
      <div className="flex-1" />

      {/* Row 4: Avatar + time range */}
      <div className="flex items-center gap-1.5">
        {wo.assignedToUserName && (
          <div
            className="flex-shrink-0 rounded-full flex items-center justify-center"
            style={{
              width: 18,
              height: 18,
              backgroundColor: getAvatarColor(wo.assignedToUserName),
            }}
          >
            <span className="font-sora text-[7px] font-bold text-white leading-none">
              {getInitials(wo.assignedToUserName)}
            </span>
          </div>
        )}
        {timeRange && (
          <span className="font-mono text-[9px] text-mid-muted whitespace-nowrap">
            {timeRange}
          </span>
        )}
      </div>
    </div>
  );
};

export default ScheduleEventContent;
